import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Stack,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  PhotoCamera,
  CloudUpload,
  Close as CloseIcon,
  ReceiptLong,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import CaseAutocomplete, { CaseAutocompleteOption } from '../Common/CaseAutocomplete';
import { todayDateInputValue } from '../../utils/datetimeLocal';
import { getAuthToken } from '../../lib/authToken';

interface ReceiptExpenseUploadProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultCaseId?: string;
  defaultClientId?: string;
}

const ReceiptExpenseUpload: React.FC<ReceiptExpenseUploadProps> = ({
  open,
  onClose,
  onSuccess,
  defaultCaseId = '',
  defaultClientId = '',
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const [caseId, setCaseId] = useState(defaultCaseId);
  const [clientId, setClientId] = useState(defaultClientId);
  const [selectedCase, setSelectedCase] = useState<CaseAutocompleteOption | null>(null);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => todayDateInputValue());
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setCaseId(defaultCaseId || '');
      setClientId(defaultClientId || '');
      setSelectedCase(defaultCaseId ? { id: defaultCaseId } : null);
      setAmount('');
      setDate(todayDateInputValue());
      setDescription('');
      setFile(null);
      setPreviewUrl(null);
      setError('');
    }
  }, [open, defaultCaseId, defaultClientId]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [file]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.files?.[0];
    if (!next) return;
    if (!next.type.startsWith('image/') && next.type !== 'application/pdf') {
      setError('Please upload an image or PDF receipt');
      return;
    }
    if (next.size > 10 * 1024 * 1024) {
      setError('File must be under 10MB');
      return;
    }
    setError('');
    setFile(next);
    if (!description) {
      setDescription(`Receipt — ${next.name}`);
    }
  };

  const handleSubmit = async () => {
    setError('');
    const parsedAmount = parseFloat(amount);
    if (!caseId) {
      setError('Select a case');
      return;
    }
    if (!clientId && !selectedCase?.clientId && !selectedCase?.client?.id) {
      setError('Selected case has no client — pick another case');
      return;
    }
    if (!parsedAmount || parsedAmount <= 0) {
      setError('Enter a valid amount');
      return;
    }
    if (!file) {
      setError('Attach a receipt photo or PDF');
      return;
    }

    const resolvedClientId = clientId || selectedCase?.clientId || selectedCase?.client?.id;
    if (!resolvedClientId) {
      setError('Selected case has no client — pick another case');
      return;
    }
    setSubmitting(true);

    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('documents', file);
      formData.append('title', description || `Expense receipt — $${parsedAmount.toFixed(2)}`);
      formData.append('description', description || 'Expense receipt');
      formData.append('documentType', 'receipt');
      formData.append('category', 'expense');
      formData.append('caseId', caseId);
      formData.append('clientId', resolvedClientId);
      formData.append('isConfidential', 'true');

      const uploadRes = await fetch('/api/documents/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const uploadJson = await uploadRes.json();
      if (!uploadRes.ok) {
        throw new Error(uploadJson.message || 'Receipt upload failed');
      }

      const receiptDoc = uploadJson.data?.documents?.[0];
      const receiptDocumentId = receiptDoc?.id;

      const billingRes = await fetch('/api/case-billing-items', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          caseId,
          clientId: resolvedClientId,
          type: 'expense',
          date,
          description: description || `Expense — $${parsedAmount.toFixed(2)}`,
          timekeeper: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : undefined,
          hours: 1,
          rate: parsedAmount,
          amount: parsedAmount,
          billable: true,
          metadata: {
            receiptDocumentId,
            receiptFileName: receiptDoc?.fileName || file.name,
            source: 'receipt_upload',
          },
        }),
      });
      const billingJson = await billingRes.json();
      if (!billingRes.ok) {
        throw new Error(billingJson.message || 'Failed to create expense');
      }

      toast.success('Receipt uploaded and expense logged');
      onSuccess?.();
      onClose();
    } catch (err: any) {
      const message = err?.message || 'Failed to save expense';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLong color="primary" />
          Upload Expense Receipt
        </Box>
        <IconButton onClick={onClose} edge="end" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <CaseAutocomplete
            label="Case"
            required
            value={selectedCase || caseId || null}
            onChange={(c) => {
              setSelectedCase(c);
              setCaseId(c?.id || '');
              setClientId(c?.clientId || c?.client?.id || '');
            }}
          />

          <TextField
            label="Amount"
            type="number"
            required
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
          />

          <TextField
            label="Date"
            type="date"
            fullWidth
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Court filing fee, courier, travel"
          />

          <Box
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Take a photo or choose a receipt file (image / PDF)
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center">
              <Button
                variant="contained"
                component="label"
                startIcon={<PhotoCamera />}
              >
                Use Camera
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileChange}
                />
              </Button>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
              >
                Choose File
                <input
                  hidden
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleFileChange}
                />
              </Button>
            </Stack>
            {file && (
              <Typography variant="caption" display="block" sx={{ mt: 1.5 }}>
                Selected: {file.name}
              </Typography>
            )}
            {previewUrl && (
              <Box
                component="img"
                src={previewUrl}
                alt="Receipt preview"
                sx={{
                  mt: 2,
                  maxWidth: '100%',
                  maxHeight: 220,
                  borderRadius: 1,
                  objectFit: 'contain',
                }}
              />
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save Expense'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptExpenseUpload;
